import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Download, Send, CheckCircle2, XCircle, MessageSquare, ScrollText, Paperclip, Building2, CalendarClock, Pencil, BellRing, FolderArchive, X, RefreshCw } from "lucide-react";
import PortalNav from "../components/PortalNav";
import { StatusBadge, isOverdue, OverdueBadge, STATUS } from "../components/shared";
import EditRequestModal from "../components/EditRequestModal";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { API } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [req, setReq] = useState(null);
  const [audit, setAudit] = useState([]);
  const [comment, setComment] = useState("");
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [uploadingTask, setUploadingTask] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const fileInputs = useRef({});
  const replaceInputs = useRef({});

  const load = async () => {
    try {
      const [r, a] = await Promise.all([api.get(`/requests/${id}`), api.get(`/requests/${id}/audit`)]);
      setReq(r.data.request); setAudit(a.data.audit);
    } catch (e) { toast({ title: "Could not load request", variant: "destructive" }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isUploader = user?.role === "client" || user?.role === "admin";
  const isReviewer = user?.role === "employee" || user?.role === "admin";

  const handleUpload = async (taskId, file) => {
    if (!file) return;
    setUploadingTask(taskId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/requests/${id}/tasks/${taskId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "File uploaded", description: file.name });
      load();
    } catch (e) {
      toast({ title: "Upload failed", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setUploadingTask(null); }
  };

  const download = async (fileId, filename) => {
    try {
      const res = await api.get(`/requests/${id}/files/${fileId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: "Download failed", variant: "destructive" }); }
  };

  const removeFile = async (fileId) => {
    try { await api.delete(`/requests/${id}/files/${fileId}`); toast({ title: "File removed" }); load(); }
    catch (e) { toast({ title: "Could not remove file", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };

  const handleReplace = async (taskId, fileId, file) => {
    if (!file) return;
    setUploadingTask(taskId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/requests/${id}/tasks/${taskId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.delete(`/requests/${id}/files/${fileId}`);
      toast({ title: "File replaced", description: file.name });
      load();
    } catch (e) {
      toast({ title: "Replace failed", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setUploadingTask(null); }
  };

  const submit = async () => {
    try { await api.post(`/requests/${id}/submit`); toast({ title: "Submitted for review" }); load(); }
    catch (e) { toast({ title: "Submit failed", variant: "destructive" }); }
  };

  const review = async (action) => {
    try {
      await api.post(`/requests/${id}/review`, { action, feedback: rejectFeedback });
      toast({ title: action === "accept" ? "Accepted & closed" : "Rejected with feedback" });
      setRejectFeedback(""); load();
    } catch (e) { toast({ title: "Action failed", variant: "destructive" }); }
  };

  const sendReminder = async () => {
    try {
      const { data } = await api.post(`/requests/${id}/remind`);
      toast({ title: "Reminder sent", description: `Emailed ${(data.recipients || []).join(", ")}` });
      load();
    } catch (e) { toast({ title: "Could not send reminder", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
  };

  const downloadZip = async () => {
    try {
      const res = await api.get(`/requests/${id}/download-zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${(req?.title || "request").replace(/[^a-z0-9 \-_.]/gi, "_")}.zip`; a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: "Downloading ZIP" });
    } catch (e) { toast({ title: "Download failed", description: e?.response?.data?.detail || "No files to download.", variant: "destructive" }); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/requests/${id}/comments`, { text: comment }); setComment(""); load(); }
    catch (e) { toast({ title: "Could not add comment", variant: "destructive" }); }
  };

  if (!req) return <div className="min-h-screen bg-slate-50/50"><PortalNav /></div>;

  const hasAnyFile = (req.tasks || []).some((t) => (t.files?.length || 0) > 0);
  const fileCount = (req.tasks || []).reduce((n, t) => n + (t.files?.length || 0), 0);
  const userSide = user?.role === "client" ? "client" : "firm";
  const canRemove = (f) =>
    isUploader &&
    [STATUS.PENDING, STATUS.RESUBMIT].includes(req.status) &&
    (f.side || "client") === userSide;
  const canSubmit = isUploader && [STATUS.PENDING, STATUS.RESUBMIT].includes(req.status) && hasAnyFile;
  const canReview = isReviewer && req.status === STATUS.SUBMITTED;

  return (
    <div className="min-h-screen bg-slate-50/50">
      <PortalNav title={user?.role === "admin" ? "Admin" : user?.role === "employee" ? "Employee" : "Client"} />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Back</button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-400"><Building2 className="h-4 w-4" /> {req.client_name}</div>
            <h1 className="mt-1 text-2xl font-bold text-[#1a1a2e]">{req.seq != null && <span data-testid="req-seq" className="mr-2 font-mono text-xl font-semibold text-slate-400">#{req.seq}</span>}{req.title}</h1>
            <p className="mt-1 max-w-2xl text-slate-500">{req.description || "No description provided."}</p>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> {req.deadline ? `Due ${req.deadline}` : "No deadline"}</span>
              <span>Requested by: <span className="font-medium text-slate-700">{req.created_by || req.handler_name}</span></span>
              <span>Uploads by: <span className="font-medium text-slate-700">{(req.assignee_names && req.assignee_names.length ? req.assignee_names.join(", ") : (req.assignee_emails && req.assignee_emails.length ? req.assignee_emails.join(", ") : (req.uploader_name || req.assignee_name)))}</span></span>
            </div>
          </div>
          <StatusBadge status={req.status} />
          {isOverdue(req) && <OverdueBadge className="ml-2" />}
        </div>

        {isReviewer && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {req.status !== STATUS.CLOSED && (
              <Button data-testid="edit-request-btn" variant="outline" size="sm" onClick={() => setShowEdit(true)} className="rounded-xl border-slate-300"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit request</Button>
            )}
            {req.status !== STATUS.CLOSED && (
              <Button data-testid="send-reminder-btn" variant="outline" size="sm" onClick={sendReminder} className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50"><BellRing className="mr-1.5 h-3.5 w-3.5" /> Send reminder now</Button>
            )}
            {hasAnyFile && (
              <Button data-testid="download-zip-btn" variant="outline" size="sm" onClick={downloadZip} className="rounded-xl border-slate-300"><FolderArchive className="mr-1.5 h-3.5 w-3.5" /> Download all (ZIP)</Button>
            )}
            {req.last_reminder_at && (
              <span data-testid="reminder-sent-indicator" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Reminder sent on {(req.last_reminder_at || "").slice(0, 10)}</span>
            )}
          </div>
        )}

        {req.status === STATUS.RESUBMIT && req.feedback && (
          <div className="mt-5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span className="font-semibold">Returned for changes:</span> {req.feedback}</div>
        )}

        <div className="mt-8 grid gap-8 lg:grid-cols-3">
          {/* Tasks + files */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-bold text-[#1a1a2e]">Tasks & documents</h2>
            {(req.tasks || []).length === 0 && <p className="text-sm text-slate-500">No tasks in this request.</p>}
            {(req.tasks || []).map((t, i) => (
              <div key={t.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-xs font-bold text-indigo-700">{i + 1}</span>
                    <span className="font-semibold text-[#1a1a2e]">{t.title}</span>
                  </div>
                  {isUploader && req.status !== STATUS.CLOSED && (
                    <>
                      <input ref={(el) => (fileInputs.current[t.id] = el)} type="file" className="hidden" onChange={(e) => handleUpload(t.id, e.target.files[0])} />
                      <Button size="sm" variant="outline" disabled={uploadingTask === t.id} onClick={() => fileInputs.current[t.id]?.click()} className="rounded-xl border-slate-300">
                        <Upload className="mr-1.5 h-3.5 w-3.5" /> {uploadingTask === t.id ? "Uploading..." : "Upload"}
                      </Button>
                    </>
                  )}
                </div>
                {(t.files?.length || 0) > 0 ? (
                  <ul className="mt-3 space-y-2">
                    {t.files.map((f) => (
                      <li key={f.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                        <span className="inline-flex items-center gap-2 text-sm text-slate-700"><Paperclip className="h-4 w-4 text-slate-400" /> {f.filename} <span className="text-xs text-slate-400">({Math.round((f.size || 0) / 1024)} KB)</span></span>
                        <div className="flex items-center gap-3">
                          <button data-testid={`download-file-${f.id}`} onClick={() => download(f.id, f.filename)} className="inline-flex items-center gap-1 text-xs font-medium text-[#2f2b8f] hover:underline"><Download className="h-3.5 w-3.5" /> Download</button>
                          {canRemove(f) && (
                            <>
                              <input ref={(el) => (replaceInputs.current[f.id] = el)} type="file" className="hidden" onChange={(e) => handleReplace(t.id, f.id, e.target.files[0])} />
                              <button data-testid={`replace-file-${f.id}`} disabled={uploadingTask === t.id} onClick={() => replaceInputs.current[f.id]?.click()} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"><RefreshCw className="h-3.5 w-3.5" /> Replace</button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button data-testid={`remove-file-${f.id}`} title="Remove file" aria-label="Remove file" className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"><X className="h-4 w-4" /></button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="rounded-2xl">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove this file?</AlertDialogTitle>
                                    <AlertDialogDescription>“{f.filename}” will be permanently removed. You can only do this before submitting for review.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                                    <AlertDialogAction data-testid={`confirm-remove-file-${f.id}`} onClick={() => removeFile(f.id)} className="rounded-xl bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <p className="mt-2 text-xs text-slate-400">No files uploaded yet.</p>}
              </div>
            ))}

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-2">
              {canSubmit && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="submit-for-review-btn" className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Send className="mr-2 h-4 w-4" /> Submit for review</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Submit for review?</AlertDialogTitle>
                      <AlertDialogDescription>
                        You're about to submit {fileCount} {fileCount === 1 ? "file" : "files"} — your firm will be notified. Once submitted, you won't be able to remove or replace files.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="confirm-submit-btn" onClick={submit} className="rounded-xl bg-[#2f2b8f] hover:bg-[#26236f]">Submit</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {canReview && (
                <>
                  <Button onClick={() => review("accept")} className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 className="mr-2 h-4 w-4" /> Accept & close</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="rounded-xl border-rose-300 text-rose-600 hover:bg-rose-50"><XCircle className="mr-2 h-4 w-4" /> Reject</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reject documents</AlertDialogTitle>
                        <AlertDialogDescription>Let the employee know what needs fixing.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="space-y-1.5"><Label>Feedback</Label><Textarea value={rejectFeedback} onChange={(e) => setRejectFeedback(e.target.value)} placeholder="Please re-upload the signed version..." className="rounded-xl" /></div>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => review("reject")} className="rounded-xl bg-rose-600 hover:bg-rose-700">Reject</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>

            {/* Comments */}
            <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="flex items-center gap-2 font-bold text-[#1a1a2e]"><MessageSquare className="h-4 w-4" /> Comments</h3>
              <div className="mt-4 space-y-3">
                {(req.comments || []).length === 0 && <p className="text-sm text-slate-400">No comments yet.</p>}
                {(req.comments || []).map((c) => (
                  <div key={c.id} className="rounded-xl bg-slate-50 px-4 py-3">
                    <div className="flex items-center justify-between"><span className="text-sm font-semibold text-[#1a1a2e]">{c.author} <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-medium uppercase text-slate-400">{c.role}</span></span><span className="text-xs text-slate-400">{(c.at || "").replace("T", " ").slice(0, 16)}</span></div>
                    <p className="mt-1 text-sm text-slate-600">{c.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Write a comment..." className="rounded-xl" />
                <Button onClick={addComment} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>

          {/* Audit trail */}
          <div>
            {isReviewer && (req.reminder_log || []).length > 0 && (
              <div data-testid="reminder-history" className="mb-6">
                <h2 className="flex items-center gap-2 font-bold text-[#1a1a2e]"><BellRing className="h-4 w-4" /> Reminder history</h2>
                <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
                  <ul className="space-y-1">
                    {[...(req.reminder_log || [])].reverse().map((rl, i) => (
                      <li key={i} className="flex items-start gap-3 px-3 py-2.5">
                        <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" />
                        <div className="flex-1">
                          <div className="text-sm text-[#1a1a2e]">Reminder to <span className="font-medium">{(rl.recipients || []).join(", ")}</span></div>
                          <div className="mt-0.5 text-xs text-slate-400">{(rl.at || "").replace("T", " ").slice(0, 16)} · by {rl.by}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <h2 className="flex items-center gap-2 font-bold text-[#1a1a2e]"><ScrollText className="h-4 w-4" /> Audit trail</h2>
            <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-2 shadow-sm">
              <ul className="space-y-1">
                {audit.map((e) => (
                  <li key={e.id} className="flex items-start gap-3 px-3 py-2.5">
                    <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-[#2f2b8f]" />
                    <div className="flex-1">
                      <div className="text-sm text-[#1a1a2e]"><span className="font-semibold">{e.actor}</span> <span className="text-slate-500">{e.action.replace(/_/g, " ")}</span></div>
                      {e.detail && <div className="text-xs text-slate-500">{e.detail}</div>}
                      <div className="mt-0.5 text-xs text-slate-400">{(e.timestamp || "").replace("T", " ").slice(0, 16)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>
      <EditRequestModal open={showEdit} onClose={() => setShowEdit(false)} request={req} onUpdated={load} />
    </div>
  );
};

export default RequestDetail;
